import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Typography,
  Box,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { EmailTemplate } from '../../services/email-templates-api';
import { formatRelativeTime } from '../../utils/dateUtils';

interface Props {
  templates: EmailTemplate[];
  onEdit: (template: EmailTemplate) => void;
  onDelete: (templateId: number) => void;
  onRefresh: () => void;
}

const EmailTemplateManagement: React.FC<Props> = ({
  templates,
  onEdit,
  onDelete,
}) => {
  if (templates.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No email templates yet. Create your first template to get started.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Subject</TableCell>
            <TableCell>Variables</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Usage</TableCell>
            <TableCell>Created</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {templates.map((template) => (
            <TableRow key={template.id} hover>
              <TableCell>
                <Typography fontWeight="medium">{template.name}</Typography>
              </TableCell>
              <TableCell>
                <Typography
                  sx={{
                    maxWidth: 250,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {template.subject}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {template.variables.slice(0, 3).map((v) => (
                    <Chip key={v} label={v} size="small" variant="outlined" />
                  ))}
                  {template.variables.length > 3 && (
                    <Chip
                      label={`+${template.variables.length - 3}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={template.is_active ? 'Active' : 'Inactive'}
                  color={template.is_active ? 'success' : 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell>{template.usage_count || 0}</TableCell>
              <TableCell>
                {formatRelativeTime(template.created_at)}
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Edit">
                  <IconButton onClick={() => onEdit(template)} size="small">
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={() => onDelete(template.id)}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EmailTemplateManagement;
